import axiosInstance from './config/api';
import {
    WorkflowData,
    PromptsResponse,
    QueueCadenceSettings,
    TestGenericQualificationRequest,
    TestGenericQualificationResponse,
} from 'pages/WorkflowBuilder/WorkflowBuilder.types';
export const createAgenticWorkflow = (data: WorkflowData) => {
    return axiosInstance.post<{ id: string }>('/workflows', data).then((res) => res.data);
};

export const getEditAgenticWorkflow = (id: string) => {
    return axiosInstance.get<WorkflowData>(`/workflows/edit/${id}`).then((res) => res.data);
};

export const deleteAgenticWorkflow = (id: string) => {
    return axiosInstance.delete<{ message: string }>(`/workflows/${id}`).then((res) => res.data);
};

export const updateAgenticWorkflow = (id: string, data: WorkflowData) => {
    return axiosInstance.put<{ id: string }>(`/workflows/${id}`, data).then((res) => res.data);
};

/** Partial update: keys left out keep whatever the workflow already stores. */
export type WorkflowConfigUpdate = {
    crmCreateContactOnInbound?: boolean;
    queueCadence?: QueueCadenceSettings;
};

export const updateWorkflowConfig = (id: string, config: WorkflowConfigUpdate) => {
    return axiosInstance.put(`/workflows/${id}/config`, config).then((res) => res.data);
};

export type LeadFieldDefinition = {
    key: string;
    label?: string;
    required?: boolean;
    example?: string;
    description?: string;
};

/** One configurable party status. Terminal statuses stop further outreach. */
export type PartyStatusDefinition = {
    name: string;
    terminal?: boolean;
    crmIntegrationName?: string;
    pushToCrm?: boolean;
};

/** One condition row of a status transition. */
export type TransitionRule = {
    variable: 'disposition' | 'outbound_status';
    condition: 'last_contains' | 'last_not_contains' | 'history_contains' | 'history_not_contains';
    value: string;
};

/**
 * One transition: when its rules hold, the party moves to target.
 * Evaluated in array order at end-of-node post-processing; first match wins.
 */
export type StatusTransition = {
    rules: TransitionRule[];
    /** Joins the rules. Omitted means 'and'. */
    logic?: 'and' | 'or';
    target: string;
};

export type WorkflowConfigResponse = {
    config: {
        leadFields?: LeadFieldDefinition[];
        partyStatuses?: PartyStatusDefinition[];
        statusTransitions?: StatusTransition[];
        crmEnabled?: boolean;
        [k: string]: unknown;
    } | null;
    effectiveOperatingHours?: unknown;
};

export const getWorkflowConfig = (id: string) => {
    return axiosInstance.get<WorkflowConfigResponse>(`/workflows/${id}/config`).then((res) => res.data);
};

export const updateWorkflowStatus = (id: string) => {
    return axiosInstance.put(`/workflows/status/${id}`).then((res) => res.data);
};

export const syncWorkflowPostProcessingConfig = (id: string) => {
    return axiosInstance.put(`/workflows/${id}/post-processing-config`).then((res) => res.data);
};

export const getWorkflowTranscript = (callId: string) => {
    return axiosInstance.get(`/workflows/transcripts/${callId}`).then((res) => res.data);
};

export const getWorkflowAudio = (callId: string) => {
    return axiosInstance
        .get(`/workflows/audio/${callId}`, {
            responseType: 'arraybuffer',
        })
        .then((res) => res.data)
        .then((data) => new Blob([data], { type: 'audio/ogg' }));
};

export const getPrompts = () => {
    return axiosInstance.get<PromptsResponse>('/workflows/node-configs').then((res) => res.data);
};

export const deleteCalls = (callIds: string[]) => {
    return axiosInstance.delete('/workflows/calls', { data: { callIds } }).then((res) => res.data);
};

export const deleteSMS = (smsIds: string[]) => {
    return axiosInstance.delete('/sms', { data: { smsIds } }).then((res) => res.data);
};

export const postTestGenericQualification = ({ transcript, config }: TestGenericQualificationRequest) => {
    return axiosInstance
        .post<TestGenericQualificationResponse>('/workflows/test/generic-qualification', { transcript, config })
        .then((res) => res.data);
};
